import { ContractStatusEnum, IQuoteBasicRespSchema } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CONTRACT_STATUS_ACCENT_COLOR } from '../../../constants/contracts';
import { IContractWithBillGroup } from '../../../hooks/AccountDetail';
import { ContractLineageWithBillGroups } from '../../../hooks/useContractLineages';
import { QuoteStatusEnumDisplay } from '../../../constants/quotes';
import { getTermAriaLabel } from '../../../utils/contractLineage';
import { getQuoteLink } from '../../../utils/quotes';
import {
  MBox,
  MButton,
  MFlex,
  MHStack,
  MSkeleton,
  MText,
  MTooltip,
  MVStack,
} from '../../Monetize';
import { MCurrency } from '../../Monetize/MCurrency';
import { ContractStatusTag } from './ContractStatusTag';
import { ContractTermLabel } from './ContractTermLabel';
import { RENEWAL_ACCENT_COLOR, RenewalQuoteDetails } from './RenewalQuoteChip';
import { MAX_VISIBLE_RENEWAL_QUOTES } from './RenewalQuoteStack';

interface ContractLineageRailProps {
  lineage?: ContractLineageWithBillGroups;
  isLoading: boolean;
  viewedContractId: string;
  onSelect: (contractId: string) => void;
  /** Renewal quotes not yet processed into a term, listed after the last term */
  renewalQuotes?: IQuoteBasicRespSchema[];
}

/**
 * Line joining one node's dot to the next; dashed toward work not yet in
 * effect. `top` is where this node's dot ends, `bottom` reaches the next dot.
 */
const RailConnector = ({
  isDashed,
  top = '26px',
  bottom = '-14px',
}: {
  isDashed?: boolean;
  top?: string;
  bottom?: string;
}) => (
  <MBox
    aria-hidden
    position="absolute"
    left="27px"
    top={top}
    bottom={bottom}
    w="2px"
    {...(isDashed
      ? {
          borderLeftWidth: '2px',
          borderLeftStyle: 'dashed',
          borderLeftColor: 'border.default',
        }
      : { bg: 'border.subtle' })}
  />
);

/**
 * Vertical list of every term in a lineage, oldest first, then any renewal
 * quotes in progress. The viewed term is highlighted; selecting another term
 * swaps the details beside the rail, and a renewal quote opens the quote.
 */
export const ContractLineageRail = ({
  lineage,
  isLoading,
  viewedContractId,
  onSelect,
  renewalQuotes = [],
}: ContractLineageRailProps) => {
  const [showAllRenewalQuotes, setShowAllRenewalQuotes] = useState(false);
  const visibleRenewalQuotes = showAllRenewalQuotes
    ? renewalQuotes
    : renewalQuotes.slice(0, MAX_VISIBLE_RENEWAL_QUOTES);
  const hiddenRenewalQuoteCount =
    renewalQuotes.length - visibleRenewalQuotes.length;

  return (
    <MBox
      as="nav"
      aria-label="Contract lineage"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.subtle"
      borderRadius="md"
      bg="bg.surface"
      overflow="hidden"
    >
      <MFlex
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor="border.subtle"
        justify="space-between"
        align="center"
      >
        <MText
          fontSize="xs"
          fontWeight="bold"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color="fg.muted"
        >
          Lineage
        </MText>
        {lineage && (
          <MText fontSize="xs" color="fg.subtle">
            Oldest first
          </MText>
        )}
      </MFlex>
      {isLoading && (
        <MVStack p={4} gap={3} align="stretch">
          <MSkeleton h="64px" />
          <MSkeleton h="64px" />
        </MVStack>
      )}
      {!isLoading && !lineage && (
        <MText px={4} py={3} color="fg.muted">
          Lineage is unavailable for this contract.
        </MText>
      )}
      {!isLoading && lineage && (
        <MBox as="ol" listStyleType="none" m={0} py={2}>
          {lineage.contracts.map((contract, index) => (
            <ContractLineageRailNode
              key={contract.id}
              contract={contract}
              termNumber={index + 1}
              termCount={lineage.contracts.length}
              isViewed={contract.id === viewedContractId}
              isCurrent={contract.id === lineage.currentContractId}
              isUpcoming={contract.id === lineage.upcomingContractId}
              isLast={index === lineage.contracts.length - 1}
              hasRenewalQuotes={renewalQuotes.length > 0}
              showBillGroup={lineage.billGroupIds.length > 1}
              onSelect={onSelect}
            />
          ))}
          {visibleRenewalQuotes.map((quote, index) => (
            <RenewalQuoteRailNode
              key={quote.id}
              quote={quote}
              isLast={index === visibleRenewalQuotes.length - 1}
            />
          ))}
          {hiddenRenewalQuoteCount > 0 && (
            <MBox as="li" px={4} py={1} pl="44px">
              <MButton
                variant="link"
                size="xs"
                onClick={() => setShowAllRenewalQuotes(true)}
                data-testid="contract-lineage-renewal-quotes-more-btn"
              >
                +{hiddenRenewalQuoteCount} more
              </MButton>
            </MBox>
          )}
        </MBox>
      )}
    </MBox>
  );
};

interface ContractLineageRailNodeProps {
  contract: IContractWithBillGroup;
  termNumber: number;
  termCount: number;
  isViewed: boolean;
  isCurrent: boolean;
  isUpcoming: boolean;
  isLast: boolean;
  /** The last term then connects down to the renewal quotes */
  hasRenewalQuotes: boolean;
  showBillGroup: boolean;
  onSelect: (contractId: string) => void;
}

const ContractLineageRailNode = memo(
  ({
    contract,
    termNumber,
    termCount,
    isViewed,
    isCurrent,
    isUpcoming,
    isLast,
    hasRenewalQuotes,
    showBillGroup,
    onSelect,
  }: ContractLineageRailNodeProps) => {
    const { id, status, startDate, endDate, totalValue, currency, billGroup } =
      contract;
    // Filled = in effect or ended early; ring = not started (pending) or finished
    const isFilledDot =
      status === ContractStatusEnum.ACTIVE ||
      status === ContractStatusEnum.CANCELED;

    return (
      <MBox as="li" position="relative">
        {(!isLast || hasRenewalQuotes) && <RailConnector isDashed={isLast} />}
        <MFlex
          asChild
          data-testid={`contract-lineage-node-${id}`}
          aria-label={getTermAriaLabel(contract, termNumber, termCount, {
            isCurrent,
            isUpcoming,
          })}
          aria-current={isViewed ? 'page' : undefined}
          onClick={() => onSelect(id)}
          position="relative"
          w="100%"
          gap={2.5}
          px={4}
          py={2.5}
          textAlign="left"
          bg={isViewed ? 'bg.selected' : 'transparent'}
          cursor="pointer"
          _hover={{ bg: isViewed ? 'bg.selected' : 'bg.hover' }}
          _focusVisible={{ boxShadow: 'focus', outline: 'none' }}
          _before={
            isViewed
              ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  w: '3px',
                  bg: 'border.accent',
                }
              : undefined
          }
        >
          <button type="button">
            <MBox
              aria-hidden
              flexShrink={0}
              w="12px"
              h="12px"
              mt={1}
              mx="6px"
              borderRadius="full"
              borderWidth="2px"
              borderStyle="solid"
              borderColor={CONTRACT_STATUS_ACCENT_COLOR[status]}
              bg={
                isFilledDot
                  ? CONTRACT_STATUS_ACCENT_COLOR[status]
                  : 'bg.surface'
              }
              position="relative"
              zIndex={1}
            />
            <MBox minW={0}>
              <ContractTermLabel
                termNumber={termNumber}
                isCurrent={isCurrent}
                isUpcoming={isUpcoming}
              />
              <MText fontWeight="semibold" color="fg.strong" mt={0.5}>
                {toDateShort(startDate)} – {toDateShort(endDate)}
              </MText>
              <MHStack gap={2} mt={1} fontSize="xs" color="fg.muted">
                <ContractStatusTag status={status} hasTooltip={false} />
                <MCurrency
                  as="span"
                  amount={totalValue}
                  options={{ currency }}
                  fontSize="inherit"
                  color="inherit"
                />
              </MHStack>
              {showBillGroup && billGroup?.name && (
                <MText fontSize="xs" color="fg.subtle" mt={1} lineClamp={1}>
                  {billGroup.name}
                </MText>
              )}
            </MBox>
          </button>
        </MFlex>
      </MBox>
    );
  },
);

/**
 * A renewal quote that has not become a term yet; links to the quote. Two
 * lines, smaller than a term, with the quote's name and id in a tooltip.
 */
const RenewalQuoteRailNode = memo(
  ({ quote, isLast }: { quote: IQuoteBasicRespSchema; isLast: boolean }) => {
    const {
      id,
      status,
      description,
      contractStartDate,
      contractEndDate,
      amount,
      currency,
    } = quote;
    const statusDisplay = QuoteStatusEnumDisplay[status];
    const startDate = toDateShort(contractStartDate);
    const endDate = toDateShort(contractEndDate);

    return (
      <MBox as="li" position="relative">
        {/* Dot spans 12–20px down the node; the next one starts 12px in */}
        {!isLast && <RailConnector isDashed top="20px" bottom="-12px" />}
        <MTooltip
          label={<RenewalQuoteDetails quote={quote} />}
          placement="right"
        >
          <MFlex
            asChild
            data-testid={`contract-lineage-renewal-quote-${id}`}
            aria-label={`Renewal quote ${description}, ${statusDisplay}, ${startDate} to ${endDate}`}
            gap={2.5}
            px={4}
            py={1.5}
            textDecoration="none"
            _hover={{ bg: 'bg.hover', textDecoration: 'none' }}
            _focusVisible={{ boxShadow: 'focus', outline: 'none' }}
          >
            <Link to={getQuoteLink({ quoteId: id, status })}>
              <MBox
                aria-hidden
                flexShrink={0}
                w="8px"
                h="8px"
                mt="6px"
                mx="8px"
                borderRadius="full"
                borderWidth="2px"
                borderStyle="solid"
                borderColor={RENEWAL_ACCENT_COLOR}
                bg="bg.surface"
                position="relative"
                zIndex={1}
              />
              <MBox minW={0} flex={1}>
                <MHStack gap={1.5} lineHeight="20px" fontSize="xs">
                  <MText
                    as="span"
                    fontSize="xxs"
                    fontWeight="bold"
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                    color="fg.muted"
                  >
                    Renewal quote
                  </MText>
                  <MText
                    as="span"
                    fontSize="inherit"
                    fontWeight="semibold"
                    color={RENEWAL_ACCENT_COLOR}
                  >
                    {statusDisplay}
                  </MText>
                  {amount != null && (
                    <MCurrency
                      as="span"
                      amount={amount}
                      options={{ currency }}
                      ml="auto"
                      fontSize="inherit"
                      color="fg.muted"
                    />
                  )}
                </MHStack>
                <MText fontSize="xs" color="fg.primary">
                  {startDate} – {endDate}
                </MText>
              </MBox>
            </Link>
          </MFlex>
        </MTooltip>
      </MBox>
    );
  },
);
